import { Link } from 'react-router'
import { formatMoney } from '@lib/helpers/money'
import { formatMinutesShort } from '@lib/helpers/minutes'
import { SEASON_LABELS } from '@lib/enums/season.enum'
import { Button } from '@/components/ui/button'
import { formatVisitDay } from '@/site-visits/VisitBar'
import {
  AttentionLine,
  BidSource,
  BidTrayFrame,
  OtherProperties,
  TaxLines,
  TraySection,
  TrayRow,
  countAndTime,
  type BidTrayVariant,
} from './BidTray'
import type { TreeBidTotal } from './useTreeBidTotal'

export interface TreeBidTrayProps {
  id: string
  total: TreeBidTotal
  siteName: string
  variant: BidTrayVariant
  /** The day of an earlier visit on screen, or null for the newest. */
  earlierVisitDay: string | null
  className?: string | undefined
  onClose: () => void
  /** Gray out the trees that have a time, so the ones without stand out. */
  onShowUnestimated: () => void
  onChooseProposal: (proposalId: string) => void
}

/**
 * What the tree work on a bid comes to, while the survey is being edited.
 *
 * The headline first, because the questions it answers are "am I over their
 * budget?" and "is this job worth it?" (the operator, 2026-11-04). Under it,
 * what this property's figure is made of, with the crew time beside the
 * money; then what is missing from it; then what is left out of it on
 * purpose, each with its own figure, so nothing quoted is invisible.
 */
export function TreeBidTray({
  id,
  total,
  siteName,
  variant,
  earlierVisitDay,
  className,
  onClose,
  onShowUnestimated,
  onChooseProposal,
}: TreeBidTrayProps) {
  const { bidContext, site, others } = total
  const bid = bidContext.bid
  const coversBid = bid !== null && bid.version.sites.length > 1
  const nextSeason = total.work.nextSeason
  const hasWork =
    site.pruning.trees + site.removals.trees + site.treatments.trees > 0

  return (
    <BidTrayFrame
      id={id}
      title={bid === null ? 'Tree work on this property' : 'Tree work on this bid'}
      variant={variant}
      onClose={onClose}
      className={className}
    >
      <BidSource
        context={bidContext}
        work="trees"
        onChooseProposal={onChooseProposal}
      />

      <div className="border-b border-default px-3 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm text-secondary-text">
            {coversBid ? 'Whole bid' : 'Total'}
          </span>
          <span
            className={
              total.isIncomplete
                ? 'text-xl font-semibold tabular-nums text-warning'
                : 'text-xl font-semibold tabular-nums'
            }
          >
            {total.totalCents === null ? '—' : formatMoney(total.totalCents)}
            {total.isIncomplete && total.totalCents !== null ? '+' : null}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-muted-text">
          {total.taxCents === null ? 'No tax added' : 'Tax included'}
          {nextSeason === null
            ? null
            : ` · the next visit, in ${SEASON_LABELS[nextSeason].toLowerCase()}`}
          {site.minutes > 0
            ? ` · ${formatMinutesShort(site.minutes) ?? ''}${site.unestimated > 0 ? '+' : ''} of crew time here`
            : null}
        </p>
        {earlierVisitDay === null ? null : (
          <p className="mt-1 text-xs text-secondary-text">
            These are the figures for the visit of{' '}
            {formatVisitDay(earlierVisitDay)}, the one on screen.
          </p>
        )}
      </div>

      {site.unestimated > 0 || total.rateIsMissing ? (
        <TraySection title="Missing from the total">
          {site.unestimated > 0 ? (
            <AttentionLine
              action={
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs"
                  onClick={onShowUnestimated}
                >
                  Show them
                </Button>
              }
            >
              {site.unestimated === 1
                ? '1 tree has no time yet.'
                : `${String(site.unestimated)} trees have no time yet.`}
            </AttentionLine>
          ) : null}
          {total.rateIsMissing ? (
            <AttentionLine
              action={
                <Link
                  to="/settings"
                  className="shrink-0 text-xs font-medium text-brand underline-offset-2 hover:underline"
                >
                  Settings
                </Link>
              }
            >
              No crew rate is set, so time is not priced.
            </AttentionLine>
          ) : null}
        </TraySection>
      ) : null}

      <TraySection
        title={coversBid ? `This property · ${siteName}` : siteName}
        aside={
          coversBid ? (
            <span className="text-sm font-semibold tabular-nums">
              {formatMoney(site.countedCents)}
              {site.unestimated > 0 ? '+' : null}
            </span>
          ) : null
        }
      >
        {hasWork ? (
          <>
            {site.pruning.trees > 0 ? (
              <TrayRow
                label="Pruning"
                detail={countAndTime(site.pruning.trees, 'tree', site.pruning.minutes)}
                cents={site.pruning.cents}
              />
            ) : null}
            {site.removals.trees > 0 ? (
              <TrayRow
                label="Removals"
                detail={countAndTime(site.removals.trees, 'tree', site.removals.minutes)}
                cents={site.removals.cents}
              />
            ) : null}
            {site.treatments.trees > 0 ? (
              <TrayRow
                label="Treatments"
                detail={countAndTime(
                  site.treatments.trees,
                  'tree',
                  site.treatments.minutes,
                )}
                cents={site.treatments.cents}
              />
            ) : null}
            {total.handPricedCount > 0 ? (
              <p className="pt-0.5 text-xs text-muted-text">
                {total.handPricedCount === 1
                  ? '1 price set by hand'
                  : `${String(total.handPricedCount)} prices set by hand`}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-muted-text">
            Nothing here counts toward the total yet.
          </p>
        )}
      </TraySection>

      {others.state === 'none' || bid === null ? null : (
        <OtherProperties
          others={others}
          mapPath="survey"
          proposalId={bid.proposal.id}
        />
      )}

      <TaxLines
        subtotalCents={total.subtotalCents}
        taxRatePercent={total.taxRatePercent}
        taxCents={total.taxCents}
        totalCents={total.totalCents}
        isIncomplete={total.isIncomplete}
      />

      {site.optional.trees > 0 ||
      site.removalAlternatives.trees > 0 ||
      total.laterSeasons.length > 0 ? (
        <TraySection title="Not in the total">
          {site.optional.trees > 0 ? (
            <TrayRow
              label="Optional work"
              detail={countAndTime(site.optional.trees, 'tree', null)}
              cents={site.optional.cents}
              muted
            />
          ) : null}
          {site.removalAlternatives.trees > 0 ? (
            <TrayRow
              label="Removal instead of pruning"
              detail={`${countAndTime(site.removalAlternatives.trees, 'tree', null)}, if the client chooses it`}
              cents={site.removalAlternatives.cents}
              muted
            />
          ) : null}
          {total.laterSeasons.map((later) => (
            <TrayRow
              key={later.season}
              label={`${SEASON_LABELS[later.season]} work`}
              detail={countAndTime(later.trees, 'tree', null)}
              cents={later.cents}
              muted
            />
          ))}
        </TraySection>
      ) : null}
    </BidTrayFrame>
  )
}
